import { DatePicker } from "@app/components/DatePicker.tsx";
import { StaffingDetailsModel } from "@app/data/engine.ts";
import useStaffData from "@app/data-hooks/use-staff-data.ts";
import { staffingRatesTableDefinition } from "@app/table-definitions/staff-rates.column-definition.tsx";
import { Box, Button, Checkbox, FormControlLabel, Grid2, TextField } from "@mui/material";
import { useForm } from "@tanstack/react-form";
import { CurrencyInput, DataTable } from "bi-lib";
import moment from "moment";
import { useEffect, useRef, useState } from "react";

interface Props {
    id?: number;
    setOnSave: (onSave: () => Promise<unknown>) => void;
}

export default function RatesForm({ id, setOnSave }: Props) {
    const { saveRate } = useStaffData();

    const [selectedRate, setSelectedRate] = useState<StaffingDetailsModel>();

    const defaultValues: any = {
        effectiveDate: selectedRate?.effectiveDate ? moment(selectedRate.effectiveDate) : null,
        billableTime: selectedRate?.billableTime,
        billableTravelExpense: selectedRate?.billableTravelExpense,
        billableOtherIndividualExpense: selectedRate?.billableOtherIndividualExpense,
        billableOfficeExpense: selectedRate?.billableOfficeExpense,
        invoiceVisibleTime: selectedRate?.invoiceVisibleTime,
        invoiceVisibleTravelExpense: selectedRate?.invoiceVisibleTravelExpense,
        invoiceVisibleOtherIndividualExpense: selectedRate?.invoiceVisibleOtherIndividualExpense,
        invoiceVisibleOfficeExpense: selectedRate?.invoiceVisibleOfficeExpense,
        costRate: selectedRate?.costRate,
        billingRate: selectedRate?.billingRate,
        internalRate: selectedRate?.internalRate,
        incentiveRate: selectedRate?.incentiveRate,
        averageHoursPerDay: selectedRate?.averageHoursPerDay,
    };

    const form = useForm({
        defaultValues,
        onSubmit: async ({ value }) =>
            await saveRate.mutateAsync({
                staffId: id,
                staffDetailId: selectedRate?.staffDetailId,
                effectiveDate: value.effectiveDate?.format("MM/DD/YYYY"),
                billableTime: value.billableTime,
                billableTravelExpense: value.billableTravelExpense,
                billableOtherIndividualExpense: value.billableOtherIndividualExpense,
                billableOfficeExpense: value.billableOfficeExpense,
                invoiceVisibleTime: value.invoiceVisibleTime,
                invoiceVisibleTravelExpense: value.invoiceVisibleTravelExpense,
                invoiceVisibleOtherIndividualExpense: value.invoiceVisibleOtherIndividualExpense,
                invoiceVisibleOfficeExpense: value.invoiceVisibleOfficeExpense,
                costRate: value.costRate?.toString(),
                billingRate: value.billingRate?.toString(),
                internalRate: value.internalRate?.toString(),
                incentiveRate: value.incentiveRate,
                averageHoursPerDay: value.averageHoursPerDay,
            }),
    });

    const ref = useRef<HTMLFormElement>(null);

    const handleSave = async () => {
        if (ref.current?.reportValidity()) {
            await form.handleSubmit();
            return;
        }
        throw new Error("Form is invalid");
    };

    useEffect(() => {
        setOnSave(handleSave);
    }, [form]);

    return (
        <>
            <Box mb={3}>
                <RatesList id={id} setSelectedRate={setSelectedRate} />
            </Box>
            <form
                ref={ref}
                onChange={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    void form.handleSubmit();
                }}
            >
                <Grid2 container spacing={1}>
                    <Grid2 size={4}>
                        <form.Field name={"effectiveDate"}>
                            {(field) => (
                                <DatePicker
                                    label={"Effective Date"}
                                    value={field.state.value}
                                    onChange={(e) => field.handleChange(e)}
                                    sx={{ width: "100%" }}
                                    required
                                />
                            )}
                        </form.Field>
                    </Grid2>
                    <Grid2 size={4}>
                        <form.Field name={"costRate"}>
                            {(field) => (
                                <CurrencyInput
                                    label={"Cost Rate"}
                                    type={"number"}
                                    value={field.state.value ?? ""}
                                    onChange={(e) => field.handleChange(e)}
                                    required
                                    fullWidth
                                />
                            )}
                        </form.Field>
                    </Grid2>
                    <Grid2 size={4}>
                        <form.Field name={"billingRate"}>
                            {(field) => (
                                <CurrencyInput
                                    label={"Billing Rate"}
                                    type={"number"}
                                    value={field.state.value ?? ""}
                                    onChange={(e) => field.handleChange(e)}
                                    required
                                    fullWidth
                                />
                            )}
                        </form.Field>
                    </Grid2>
                    <Grid2 size={4}>
                        <form.Field name={"internalRate"}>
                            {(field) => (
                                <CurrencyInput
                                    label={"Internal Rate"}
                                    type={"number"}
                                    value={field.state.value ?? ""}
                                    onChange={(e) => field.handleChange(e)}
                                    required
                                    fullWidth
                                />
                            )}
                        </form.Field>
                    </Grid2>
                    <Grid2 size={4}>
                        <form.Field name={"incentiveRate"}>
                            {(field) => (
                                <CurrencyInput
                                    label={"Incentive Rate"}
                                    type={"number"}
                                    value={field.state.value ?? ""}
                                    onChange={(e) => field.handleChange(e)}
                                    required
                                    fullWidth
                                />
                            )}
                        </form.Field>
                    </Grid2>
                    <Grid2 size={4}>
                        <form.Field name={"averageHoursPerDay"}>
                            {(field) => (
                                <TextField
                                    label={"Average Hours per Day"}
                                    type={"number"}
                                    value={field.state.value ?? ""}
                                    onChange={(e) => field.handleChange(+e.target.value)}
                                    required
                                    fullWidth
                                />
                            )}
                        </form.Field>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                        <form.Field name={"billableTime"}>
                            {(field) => (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={field.state.value ?? false}
                                            onChange={(e) => field.handleChange(e.target.checked)}
                                        />
                                    }
                                    label={"Time is Billable"}
                                />
                            )}
                        </form.Field>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                        <form.Field name={"invoiceVisibleTime"}>
                            {(field) => (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={field.state.value ?? false}
                                            onChange={(e) => field.handleChange(e.target.checked)}
                                        />
                                    }
                                    label={"Time is Invoice Visible"}
                                />
                            )}
                        </form.Field>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                        <form.Field name={"billableTravelExpense"}>
                            {(field) => (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={field.state.value ?? false}
                                            onChange={(e) => field.handleChange(e.target.checked)}
                                        />
                                    }
                                    label={"Travel Expenses are Billable"}
                                />
                            )}
                        </form.Field>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                        <form.Field name={"invoiceVisibleTravelExpense"}>
                            {(field) => (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={field.state.value ?? false}
                                            onChange={(e) => field.handleChange(e.target.checked)}
                                        />
                                    }
                                    label={"Travel Expenses are Invoice Visible"}
                                />
                            )}
                        </form.Field>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                        <form.Field name={"billableOtherIndividualExpense"}>
                            {(field) => (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={field.state.value ?? false}
                                            onChange={(e) => field.handleChange(e.target.checked)}
                                        />
                                    }
                                    label={"Other Expenses are Billable"}
                                />
                            )}
                        </form.Field>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                        <form.Field name={"invoiceVisibleOtherIndividualExpense"}>
                            {(field) => (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={field.state.value ?? false}
                                            onChange={(e) => field.handleChange(e.target.checked)}
                                        />
                                    }
                                    label={"Other Expenses are Invoice Visible"}
                                />
                            )}
                        </form.Field>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                        <form.Field name={"billableOfficeExpense"}>
                            {(field) => (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={field.state.value ?? false}
                                            onChange={(e) => field.handleChange(e.target.checked)}
                                        />
                                    }
                                    label={"Office Expenses are Billable"}
                                />
                            )}
                        </form.Field>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                        <form.Field name={"invoiceVisibleOfficeExpense"}>
                            {(field) => (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={field.state.value ?? false}
                                            onChange={(e) => field.handleChange(e.target.checked)}
                                        />
                                    }
                                    label={"Office Expenses are Invoice Visible"}
                                />
                            )}
                        </form.Field>
                    </Grid2>
                </Grid2>
                <Box mt={1}>
                    <Button variant={"outlined"} onClick={() => setSelectedRate(undefined)} fullWidth>
                        Clear
                    </Button>
                </Box>
            </form>
        </>
    );
}

interface ListProps {
    id?: number;
    setSelectedRate: (rate: StaffingDetailsModel) => void;
}

function RatesList({ id, setSelectedRate }: ListProps) {
    const { ratesList, deleteRate } = useStaffData({ id });

    return (
        <DataTable
            rowsPerPageOptions={[5000]}
            label={"Staff Rates"}
            columns={staffingRatesTableDefinition({
                onEdit: setSelectedRate,
                onDelete: deleteRate.mutate,
                rowCount: ratesList.data?.length ?? 0,
            })}
            data={ratesList.data ?? []}
            queryHook={ratesList}
        />
    );
}
